/**
 * ╔══════════════════════════════════════════════════════════════════════════════════════════════╗
 * ║                            BULK APPROVAL SYSTEM                                             ║
 * ║                    Company Data Management & Approval Interface                             ║
 * ╚══════════════════════════════════════════════════════════════════════════════════════════════╝
 */

import React, { useState, useEffect } from 'react'
import { Check, X, Eye, Filter, Search, Download, Upload, Building2, Clock, ArrowLeft } from 'lucide-react'
import { DatabaseService, Company } from '../lib/database'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Alert, AlertDescription } from './ui/alert'

interface BulkApprovalProps {
  onClose?: () => void
}

interface FilterState {
  search: string
  industry: string
  employeeCount: string
  headquarters: string
  approvalStatus: 'all' | 'pending' | 'approved'
}

export const BulkApproval: React.FC<BulkApprovalProps> = ({ onClose }) => {
  // ===== STATE MANAGEMENT =====
  const [companies, setCompanies] = useState<Company[]>([])
  const [selectedCompanies, setSelectedCompanies] = useState<Set<number>>(new Set())
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    industry: '',
    employeeCount: '',
    headquarters: '',
    approvalStatus: 'pending'
  })

  const databaseService = DatabaseService.getInstance()

  // ===== DATA LOADING =====

  useEffect(() => {
    loadCompanies()
  }, [])

  const loadCompanies = async () => {
    try {
      setLoading(true)
      const allCompanies = await databaseService.getAllCompanies()
      setCompanies(allCompanies)
      console.log(`📊 Loaded ${allCompanies.length} companies`)
    } catch (err) {
      console.error('❌ Failed to load companies:', err)
      setError('Failed to load companies')
    } finally {
      setLoading(false)
    }
  }

  // ===== FILTERING =====

  const filteredCompanies = companies.filter(company => {
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      if (!company.name.toLowerCase().includes(searchLower) &&
          !company.headquarters_city?.toLowerCase().includes(searchLower) &&
          !company.headquarters_state?.toLowerCase().includes(searchLower)) {
        return false
      }
    }

    // Approval status filter
    if (filters.approvalStatus === 'pending' && company.is_approved) return false
    if (filters.approvalStatus === 'approved' && !company.is_approved) return false

    // Employee count filter
    if (filters.employeeCount) {
      const count = company.employee_count || 0
      switch (filters.employeeCount) {
        case 'small': if (count >= 100) return false; break
        case 'medium': if (count < 100 || count >= 1000) return false; break
        case 'large': if (count < 1000) return false; break
      }
    }

    return true
  })

  // ===== SELECTION MANAGEMENT =====

  const toggleCompanySelection = (companyId: number) => {
    const newSelection = new Set(selectedCompanies)
    if (newSelection.has(companyId)) {
      newSelection.delete(companyId)
    } else {
      newSelection.add(companyId)
    }
    setSelectedCompanies(newSelection)
  }

  const selectAll = () => {
    const pendingCompanies = filteredCompanies
      .filter(c => !c.is_approved)
      .map(c => c.id)
    setSelectedCompanies(new Set(pendingCompanies))
  }

  const clearSelection = () => {
    setSelectedCompanies(new Set())
  }

  // ===== BULK OPERATIONS =====

  const bulkApprove = async () => {
    if (selectedCompanies.size === 0) return

    try {
      setProcessing(true)
      const success = await databaseService.bulkApproveCompanies(Array.from(selectedCompanies))
      
      if (success) {
        console.log(`✅ Approved ${selectedCompanies.size} companies`)
        await loadCompanies() // Reload data
        setSelectedCompanies(new Set()) // Clear selection
      } else {
        setError('Failed to approve companies')
      }
    } catch (err) {
      console.error('❌ Bulk approve failed:', err)
      setError('Bulk approval failed')
    } finally {
      setProcessing(false)
    }
  }

  const bulkReject = async () => {
    if (selectedCompanies.size === 0) return
    if (!confirm(`Are you sure you want to delete ${selectedCompanies.size} companies? This cannot be undone.`)) return

    try {
      setProcessing(true)
      const success = await databaseService.bulkRejectCompanies(Array.from(selectedCompanies))
      
      if (success) {
        console.log(`🗑️ Rejected ${selectedCompanies.size} companies`)
        await loadCompanies() // Reload data
        setSelectedCompanies(new Set()) // Clear selection
      } else {
        setError('Failed to reject companies')
      }
    } catch (err) {
      console.error('❌ Bulk reject failed:', err)
      setError('Bulk rejection failed')
    } finally {
      setProcessing(false)
    }
  }

  // ===== DATA EXPORT =====

  const exportData = () => {
    const dataToExport = selectedCompanies.size > 0 
      ? filteredCompanies.filter(c => selectedCompanies.has(c.id))
      : filteredCompanies

    const csvContent = [
      ['ID', 'Name', 'Industry', 'Employee Count', 'Headquarters', 'Approved', 'Created'],
      ...dataToExport.map(c => [
        c.id,
        c.name,
        c.industry_id || '',
        c.employee_count || '',
        `${c.headquarters_city || ''}, ${c.headquarters_state || ''}`.trim().replace(/^,\s*/, ''),
        c.is_approved ? 'Yes' : 'No',
        new Date(c.created_at).toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `companies-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // ===== RENDER =====

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading companies...</p>
          </div>
        </div>
      </div>
    )
  }

  const pendingCount = companies.filter(c => !c.is_approved).length
  const approvedCount = companies.filter(c => c.is_approved).length

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <Card className="mb-8 shadow-lg border-0">
          <CardHeader className="pb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Building2 className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                    Company Data Management
                  </CardTitle>
                  <p className="text-gray-600">
                    Review and approve scraped company data for Clock-It database
                  </p>
                </div>
              </div>
              {onClose && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={onClose}
                  className="hover:bg-gray-100"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="shadow-lg border-0 bg-gradient-to-br from-amber-50 to-orange-50">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-amber-600 mb-2">{pendingCount}</div>
              <div className="text-amber-700 font-medium">Pending Approval</div>
            </CardContent>
          </Card>
          <Card className="shadow-lg border-0 bg-gradient-to-br from-emerald-50 to-green-50">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-emerald-600 mb-2">{approvedCount}</div>
              <div className="text-emerald-700 font-medium">Approved</div>
            </CardContent>
          </Card>
          <Card className="shadow-lg border-0 bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">{companies.length}</div>
              <div className="text-blue-700 font-medium">Total Companies</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-8 shadow-lg border-0">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Filter className="w-5 h-5 text-blue-600" />
              Filters & Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search companies..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="pl-10 w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Status Filter */}
              <select
                value={filters.approvalStatus}
                onChange={(e) => setFilters({ ...filters, approvalStatus: e.target.value as any })}
                className="p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending Only</option>
                <option value="approved">Approved Only</option>
              </select>

              {/* Employee Count Filter */}
              <select
                value={filters.employeeCount}
                onChange={(e) => setFilters({ ...filters, employeeCount: e.target.value })}
                className="p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Sizes</option>
                <option value="small">Small (&lt; 100)</option>
                <option value="medium">Medium (100-999)</option>
                <option value="large">Large (1000+)</option>
              </select>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button 
                  onClick={selectAll} 
                  variant="outline"
                  size="sm"
                  className="hover:bg-blue-50"
                >
                  Select All
                </Button>
                <Button 
                  onClick={clearSelection}
                  variant="outline" 
                  size="sm"
                >
                  Clear
                </Button>
              </div>

              {/* Export */}
              <Button 
                onClick={exportData}
                variant="outline"
                size="sm"
                className="flex items-center gap-2 hover:bg-green-50 border-green-200 text-green-700"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions */}
        {selectedCompanies.size > 0 && (
          <Card className="mb-8 shadow-lg border-0 bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="font-semibold text-blue-800">
                    {selectedCompanies.size} companies selected
                  </span>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={bulkApprove}
                    disabled={processing}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  >
                    <Check className="w-4 h-4" />
                    {processing ? 'Processing...' : 'Approve Selected'}
                  </Button>
                  <Button
                    onClick={bulkReject}
                    disabled={processing}
                    variant="destructive"
                    className="flex items-center gap-2 disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                    {processing ? 'Processing...' : 'Reject Selected'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error Display */}
        {error && (
          <Alert className="mb-8 bg-red-50 border-red-200">
            <AlertDescription className="text-red-800">{error}</AlertDescription>
          </Alert>
        )}        {/* Companies Cards */}
        <div className="grid gap-4">
          {filteredCompanies.map((company) => (
            <Card key={company.id} className="shadow-lg border-0 hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {!company.is_approved && (
                      <input
                        type="checkbox"
                        checked={selectedCompanies.has(company.id)}
                        onChange={() => toggleCompanySelection(company.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
                      />
                    )}
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{company.name}</h3>
                        <Badge 
                          variant={company.is_approved ? "default" : "secondary"}
                          className={company.is_approved ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}
                        >
                          {company.is_approved ? 'Approved' : 'Pending'}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Industry ID:</span> {company.industry_id || 'N/A'}
                        </div>
                        <div>
                          <span className="font-medium">Employees:</span> {company.employee_count ? company.employee_count.toLocaleString() : 'N/A'}
                        </div>
                        <div>
                          <span className="font-medium">Location:</span> {
                            company.headquarters_city && company.headquarters_state 
                              ? `${company.headquarters_city}, ${company.headquarters_state}` 
                              : 'N/A'
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => console.log('View company details:', company)}
                      className="hover:bg-blue-50"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    {!company.is_approved && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => databaseService.bulkApproveCompanies([company.id]).then(() => loadCompanies())}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            if (confirm(`Delete ${company.name}? This cannot be undone.`)) {
                              databaseService.bulkRejectCompanies([company.id]).then(() => loadCompanies())
                            }
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* No Results */}
        {filteredCompanies.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No companies found matching your filters</p>
          </div>
        )}
      </div>
    </div>
  )
}